/**
 * DangoteDashboard — Corporate EXCO view. Reads from DangoteContext.
 * Single source: useDangote() → all data from consolidated monthly output.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useDangote } from "@/context/DangoteContext";
import { PageHeader, SectionCard, MetricCard } from "@/components/ds";
import { RefreshCw, ChevronRight, AlertTriangle } from "lucide-react";

const BU_COLORS = {
  cement:"bg-red-500", refinery:"bg-orange-500",
  fertilizer:"bg-yellow-500", sugar:"bg-teal-500", logistics:"bg-blue-500",
};

// Loading skeleton identical to page layout
function DashboardSkeleton() {
  return (
    <div className="p-6 lg:p-8 space-y-6 animate-pulse">
      <div className="h-8 w-64 bg-slate-100 rounded" />
      <div className="grid grid-cols-5 gap-4">{[1,2,3,4,5].map(i=><div key={i} className="h-24 bg-slate-100 rounded-xl"/>)}</div>
      <div className="grid grid-cols-3 gap-5">
        <div className="col-span-2 h-64 bg-slate-100 rounded-xl" />
        <div className="h-64 bg-slate-100 rounded-xl" />
      </div>
    </div>
  );
}

export default function DangoteDashboard() {
  const navigate = useNavigate();
  const {
    periods, selectedPeriod, setSelectedPeriod,
    consolidated, loading,
    period, totals, byBu, topEmitters, opportunities,
    completeness, vrsScore, methodology,
  } = useDangote();

  if (loading && !consolidated) return <DashboardSkeleton />;

  if (!consolidated) return (
    <div className="p-6 lg:p-8">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
        <AlertTriangle size={20} className="text-amber-400 mx-auto mb-2" />
        <p className="text-sm font-bold text-amber-800">No Data</p>
        <p className="text-xs text-amber-600 mt-1">Run POST /api/dangote/seed or upload data via Monthly Reporting.</p>
        <button onClick={() => navigate("/corporate/reporting")}
          className="mt-3 px-4 py-2 bg-teal-600 text-white text-xs font-bold rounded-xl">
          Go to Monthly Reporting
        </button>
      </div>
    </div>
  );

  const total = totals?.total || 0;
  const STATUS_COLORS = {
    approved: "bg-teal-100 text-teal-700 border-teal-200",
    locked:   "bg-slate-100 text-slate-500 border-slate-200",
    submitted:"bg-blue-100 text-blue-700 border-blue-200",
    draft:    "bg-amber-100 text-amber-700 border-amber-200",
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Dangote Industries</p>
          <PageHeader title="Corporate Dashboard" />
          <p className="text-xs text-slate-400 mt-0.5">
            {methodology?.calculation_basis?.split("+")[0]?.trim()} · {methodology?.protocol}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select value={selectedPeriod||""}
            onChange={e => setSelectedPeriod(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700"
            data-testid="period-selector">
            {periods.map(p => <option key={p.period_id} value={p.period_id}>{p.label} — {p.status}</option>)}
          </select>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${STATUS_COLORS[period?.status]||"bg-slate-100 text-slate-500 border-slate-200"}`}>
            {period?.label} · {period?.status?.toUpperCase()}
          </span>
          {vrsScore != null && <span className="text-xs font-bold px-2 py-0.5 rounded-full border bg-teal-100 text-teal-700 border-teal-200">VRS {vrsScore}</span>}
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${completeness>=90?"bg-emerald-100 text-emerald-700 border-emerald-200":"bg-amber-100 text-amber-700 border-amber-200"}`}>
            Data {completeness}%
          </span>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <MetricCard label="Total tCO₂e" value={`${Number(total/1000).toFixed(0)}K`} unit="tCO₂e/month" color="text-slate-900" />
        <MetricCard label="Scope 1" value={`${Number((totals?.scope1||0)/1000).toFixed(0)}K`} unit="tCO₂e" color="text-red-700" />
        <MetricCard label="Scope 2" value={`${Number((totals?.scope2||0)/1000).toFixed(0)}K`} unit="tCO₂e" color="text-orange-600" />
        <MetricCard label="Reduction Pipeline" value={`${Number((opportunities?.total_reduction_tco2e_yr||0)/1000).toFixed(0)}K`} unit="tCO₂e/yr" color="text-emerald-700" />
        <MetricCard label="Indicative Carbon Value" value={`$${Number((opportunities?.carbon_revenue_usd_yr?.base_25||0)/1e6).toFixed(1)}M`} unit="/yr (conditional)" color="text-blue-700" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Emissions + Trend */}
        <div className="lg:col-span-2 space-y-5">
          <SectionCard>
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold">Emissions by Business Unit</p>
              <span className="text-xs text-slate-400">{Number(total/1000).toFixed(0)}K tCO₂e total</span>
            </div>
            {(byBu || []).map(bu => {
              const pct = total > 0 ? Math.min((bu.total/total)*100,100) : 0;
              return (
                <button key={bu.bu_id} onClick={() => navigate(`/corporate/bu/${bu.bu_id}`)}
                  className="w-full mb-3 text-left group hover:opacity-80">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600 font-semibold group-hover:text-teal-700 flex items-center gap-1">
                      {bu.bu_name} <ChevronRight size={9} className="opacity-0 group-hover:opacity-100 text-teal-500" />
                    </span>
                    <span className="font-bold text-slate-900">{Number(bu.total).toLocaleString()}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${BU_COLORS[bu.sector_type]||"bg-slate-400"}`} style={{width:`${pct}%`}} />
                  </div>
                  <p className="text-[9px] text-slate-400 mt-0.5 text-right">{pct.toFixed(1)}%</p>
                </button>
              );
            })}
            <div className="pt-3 border-t border-slate-100 flex gap-4 text-xs">
              {[["S1",totals?.scope1,"text-red-600"],["S2",totals?.scope2,"text-orange-600"],["S3",totals?.scope3,"text-purple-600"]].map(([l,v,c])=>(
                <span key={l} className={`${c} font-semibold`}>{l}: {Number(v||0).toLocaleString()}</span>
              ))}
            </div>
          </SectionCard>

          {/* Top emitters */}
          <SectionCard>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Top Emitting Assets</p>
            {(topEmitters||[]).map((e,i) => (
              <button key={`${e.asset_id}-${i}`} onClick={() => navigate(`/corporate/asset/${e.asset_id}`)}
                className="w-full flex items-center justify-between py-2.5 border-b border-slate-50 last:border-0 hover:opacity-80 text-left">
                <div className="flex items-center gap-2.5">
                  <span className="text-xs text-slate-300 w-4 font-bold">{i+1}</span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{e.asset_id}</p>
                    <p className="text-xs text-slate-400">{e.bu_name}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-red-700 tabular-nums">{Number(e.tco2e).toLocaleString()}</p>
                  <p className="text-xs text-slate-400">tCO₂e/month</p>
                </div>
              </button>
            ))}
          </SectionCard>
        </div>

        {/* Right: Quality + Pipeline + Actions */}
        <div className="space-y-4">
          <SectionCard>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Data Integrity</p>
            {[
              ["Completeness", completeness, completeness>=90?"bg-emerald-500":"bg-amber-500"],
              ["VRS Score", vrsScore, vrsScore>=75?"bg-teal-500":"bg-amber-500"],
            ].map(([l,v,c])=>(
              <div key={l} className="mb-3">
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-500">{l}</span>
                  <span className="font-bold text-slate-900">{v}/100</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${c}`} style={{width:`${v}%`}} />
                </div>
              </div>
            ))}
          </SectionCard>

          <SectionCard>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Value Pipeline</p>
            {[
              {l:"Reduction potential", v:`${Number((opportunities?.total_reduction_tco2e_yr||0)/1000).toFixed(0)}K tCO₂e/yr`, c:"text-emerald-700"},
              {l:"Indicative Carbon Value (conditional)", v:`$${Number((opportunities?.carbon_revenue_usd_yr?.base_25||0)/1e6).toFixed(1)}M/yr`, c:"text-blue-700"},
            ].map(({l,v,c})=>(
              <div key={l} className="flex justify-between text-xs py-1.5 border-b border-slate-50 last:border-0">
                <span className="text-slate-500">{l}</span>
                <span className={`font-bold ${c}`}>{v}</span>
              </div>
            ))}
            <button onClick={() => navigate("/corporate/value-bridge")}
              className="w-full mt-3 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl">
              View Opportunities →
            </button>
            <p className="text-[9px] text-slate-400 italic mt-2 text-center">
              Financial outputs derived from modeled scenarios — see Value Bridge
            </p>
          </SectionCard>

          {/* Action panel — next steps */}
          <SectionCard>
            <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Actions</p>
            {[
              {l:"Monthly Reporting", d:"Input data · workflow", path:"/corporate/reporting"},
              {l:"Climate & Circularity", d:"Scope 1/2/3 detail", path:"/corporate/climate"},
              {l:"Carbon Strategy", d:"Pathways + opportunities", path:"/corporate/carbon-strategy"},
              {l:"Financing", d:"CAPEX · IRR · carbon revenue", path:"/corporate/financing"},
              {l:"Reports", d:"Generate report pack", path:"/corporate/reports"},
            ].map(({l,d,path})=>(
              <button key={path} onClick={()=>navigate(path)}
                className="w-full flex items-center justify-between py-2 hover:text-teal-700 group">
                <div className="text-left">
                  <p className="text-xs text-slate-700 font-semibold group-hover:text-teal-700">{l}</p>
                  <p className="text-[9px] text-slate-400">{d}</p>
                </div>
                <ChevronRight size={11} className="text-slate-300 group-hover:text-teal-500 flex-shrink-0" />
              </button>
            ))}
          </SectionCard>
        </div>
      </div>
    </div>
  );
}
